import { ReadonlyVec3, vec3 } from 'gl-matrix';
import { enqueueSnackbar } from '~/utils/notifications';
import { useCallback, useState } from 'react';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { SDFOperations } from '~/engine/util/algorithms/sdf/SDFOperations';
import { Environment } from '~/engine/data/Environment';
import { getErrorMessage } from '~/utils/errors';
import { waitForUI } from '~/utils/uiUtils';
import { useGetOrCreateDestination } from '~/hooks/structure-set/useGetOrCreateDestination';

interface UseMarginApplicationOptions {
    environment: Environment | null;
    inputRoi: RegionOfInterest | undefined;
    onSuccess: () => void;
    updateView: () => void;
    /**
     * Optional: hand off a cached preview texture instead of recomputing.
     * Returns undefined if no cached result is available for the given input.
     */
    consumeSymmetricPreview?: (
        marginValueMm: number
    ) => ContourTexture | undefined;
    consumeAsymmetricPreview?: (
        negLpsMm: ReadonlyVec3,
        posLpsMm: ReadonlyVec3
    ) => ContourTexture | undefined;
    /**
     * Returns the padded "expanded" SDF the asymmetric pass reads from,
     * built once per source by the preview hook. Used when applying
     * without a cached preview to recompute.
     */
    getExpanded?: () => ContourTexture | undefined;
}

export function useMarginApplication({
    environment,
    inputRoi,
    onSuccess,
    updateView,
    consumeSymmetricPreview,
    consumeAsymmetricPreview,
    getExpanded,
}: UseMarginApplicationOptions) {
    const [isProcessing, setIsProcessing] = useState(false);
    const { getOrCreateDestination } = useGetOrCreateDestination({
        environment,
    });

    const handleSymmetricApply = useCallback(
        async (
            marginMm: number,
            outputRoiNumber: number | null,
            newName?: string
        ) => {
            if (isProcessing) {
                enqueueSnackbar(
                    'Another margin operation is already in progress.',
                    {
                        variant: 'warning',
                    }
                );
                return;
            }

            if (!environment?.structureSet || !inputRoi?.texture) {
                return;
            }

            setIsProcessing(true);

            try {
                const dest = await getOrCreateDestination(
                    outputRoiNumber,
                    newName
                );
                if (!dest) {
                    return;
                }

                const marginTexture =
                    consumeSymmetricPreview?.(marginMm) ??
                    (await SDFOperations.applySymmetricMargin(
                        inputRoi.texture,
                        marginMm
                    ));
                if (!marginTexture) {
                    enqueueSnackbar('Failed to apply margin', {
                        variant: 'error',
                    });
                    return;
                }
                dest.replaceTexture(marginTexture, 'Symmetric Margin');
                updateView();
                enqueueSnackbar('Margin applied', { variant: 'success' });
                onSuccess();
            } catch (e) {
                console.error('Apply error:', e);
                enqueueSnackbar(
                    `Failed to apply margin: ${getErrorMessage(e)}`,
                    {
                        variant: 'error',
                    }
                );
            } finally {
                setIsProcessing(false);
            }
        },
        [
            environment,
            inputRoi,
            isProcessing,
            getOrCreateDestination,
            updateView,
            onSuccess,
            consumeSymmetricPreview,
        ]
    );

    const handleAsymmetricApply = useCallback(
        async (
            negLpsMm: vec3,
            posLpsMm: vec3,
            outputRoiNumber: number | null,
            newName?: string
        ) => {
            if (isProcessing) {
                enqueueSnackbar(
                    'Another margin operation is already in progress.',
                    {
                        variant: 'warning',
                    }
                );
                return;
            }

            if (!environment?.structureSet || !inputRoi?.texture) {
                return;
            }

            setIsProcessing(true);

            try {
                const dest = await getOrCreateDestination(
                    outputRoiNumber,
                    newName
                );
                if (!dest || !dest.texture) {
                    setIsProcessing(false);
                    return;
                }

                try {
                    let result = consumeAsymmetricPreview?.(negLpsMm, posLpsMm);
                    if (!result) {
                        const expanded = getExpanded?.();
                        if (!expanded) {
                            enqueueSnackbar('Failed to apply margin', {
                                variant: 'error',
                            });
                            return;
                        }
                        enqueueSnackbar('Applying margin...', {
                            variant: 'info',
                        });
                        // TODO: improve kind of hacky rendering solutions should be removed and
                        // heavy computation should be lifted away from blocking the main thread for smooth rendering.
                        await waitForUI();
                        result = await SDFOperations.applyAsymmetricMargin(
                            expanded,
                            negLpsMm,
                            posLpsMm
                        );
                    }
                    // applyAsymmetricMargin leaves the output covering the
                    // padded grid (margin + buffer). Crop to the image
                    // before persisting to the ROI.
                    result.cropToImageBounds();
                    dest.replaceTexture(result, 'Asymmetric Margin');
                    updateView();
                    enqueueSnackbar(`Applied margin to "${dest.name}"`, {
                        variant: 'success',
                    });
                    onSuccess();
                } catch (e) {
                    console.error('Asymmetric apply error:', e);
                    enqueueSnackbar(
                        `Failed to apply margin: ${getErrorMessage(e)}`,
                        { variant: 'error' }
                    );
                } finally {
                    setIsProcessing(false);
                }
            } catch (e) {
                setIsProcessing(false);
                console.error('Asymmetric margin error:', e);
                enqueueSnackbar(
                    `Failed to apply margin: ${getErrorMessage(e)}`,
                    {
                        variant: 'error',
                    }
                );
            }
        },
        [
            environment,
            inputRoi,
            isProcessing,
            getOrCreateDestination,
            updateView,
            onSuccess,
            consumeAsymmetricPreview,
            getExpanded,
        ]
    );

    return {
        isProcessing,
        handleSymmetricApply,
        handleAsymmetricApply,
    };
}
